/**
 * Tipos do state local do wizard ZTNA + helpers de derivacao.
 *
 * Cada step tem seu sub-objeto. step_data persistido no draft tem o mesmo
 * shape — quando carregamos draft do servidor, fazemos merge com defaults.
 *
 * Hostname canonical eh derivado de slug + tenant_slug + environment via
 * `buildCanonicalHostname()`. Backend valida o shape; aqui replicamos pra
 * preview.
 */
import type { DnsLookupResponse, Environment, IdentityMode, JobPayload, Protocol, RoutingMode, TimeWindow } from '../types'

export interface WizardStep1Data {
  tenant_id: number | null
  tenant_name: string | null
  tenant_slug: string | null
  environment: Environment | null
  zone_id: string
  zone_name: string | null
  record_mode: 'create' | 'reuse'
  subdomain: string
  /** Nome completo: `${subdomain}.${zone_name}` */
  public_hostname: string
}

export interface WizardStep2Data {
  slug: string
  display_name: string
  /** Editado manualmente OU derivado do slug + tenant + env. */
  canonical_hostname: string
  /** Quando true, usuario travou o canonical e nao deriva mais. */
  canonical_locked: boolean
}

export interface WizardStep3Data {
  protocol: Protocol
  routing_mode: RoutingMode
  identity_mode: IdentityMode
  upstream_url: string
  required_permissions: string[]
  oidc_redirect_uris: string[]
  /** Gap 3 (2026-05-10): visibilidade org-default. Default TRUE = todos os membros do tenant dono veem. */
  org_default_visible: boolean
  /** Quando true, apenas funcionários da organização dona do app acessam. Default false = público. */
  is_private: boolean
  /** Restrição de horário (opcional). null = sem restrição. */
  allowed_hours: TimeWindow | null
  /** Roles que ignoram allowed_hours. Default ['admin','manager','gestor']. */
  bypass_time_window_roles: string[]
}

export interface WizardStep4Data {
  confirmed_upstream_headers: boolean
}

/**
 * Sub-projeto 4 Lote 2 — opt-in pra migrar DNS legacy quando preflight
 * check #10 detecta colisao. Ausente quando ``record_type === 'NONE'``.
 */
export interface DnsMigrationState {
  enabled: boolean
  upstream_url: string
  protocol: 'https' | 'http'
  record_checksum?: string
}

export interface WizardState {
  step1: WizardStep1Data
  step2: WizardStep2Data
  step3: WizardStep3Data
  step4: WizardStep4Data
  dnsMigration?: DnsMigrationState
  /** Resultado do dns-lookup (Step 1 trigger) — Step 3 usa pra auto-fill upstream_url. */
  dnsLookup?: DnsLookupResponse
}

export const DEFAULT_WIZARD_STATE: WizardState = {
  step1: {
    tenant_id: null,
    tenant_name: null,
    tenant_slug: null,
    environment: null,
    zone_id: '',
    zone_name: null,
    record_mode: 'create',
    subdomain: '',
    public_hostname: '',
  },
  step2: {
    slug: '',
    display_name: '',
    canonical_hostname: '',
    canonical_locked: false,
  },
  step3: {
    protocol: 'http',
    routing_mode: 'direct',
    identity_mode: 'header',
    upstream_url: '',
    required_permissions: [],
    oidc_redirect_uris: [],
    org_default_visible: true,
    is_private: false,
    allowed_hours: null,
    bypass_time_window_roles: ['admin', 'manager', 'gestor'],
  },
  step4: {
    confirmed_upstream_headers: false,
  },
  dnsMigration: undefined,
}

const ZTNA_WILDCARD_DOMAIN = 'zt.console.amazonasinovare.com.br'

/**
 * Deriva o canonical hostname conforme convencao do broker:
 *   `<slug>--<tenant_slug>--<environment>.zt.console.amazonasinovare.com.br`
 * Lowercase + replace de caracteres invalidos por dash.
 */
export function buildCanonicalHostname(
  slug: string,
  tenantSlug: string | null,
  env: Environment | null,
): string {
  if (!slug || !tenantSlug || !env) return ''
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9-]/g, '-')
  return `${norm(slug)}--${norm(tenantSlug)}--${env.toLowerCase()}.${ZTNA_WILDCARD_DOMAIN}`
}

/** Slug deve ser kebab-case ASCII. */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug) && slug.length >= 2 && slug.length <= 64
}

export function buildPublicHostname(subdomain: string, zoneName: string | null): string {
  if (!subdomain || !zoneName) return ''
  const sub = subdomain.trim().replace(/\.+$/, '')
  const zone = zoneName.replace(/\.+$/, '')
  // '@' = apex (convenção DNS): o hostname É a própria zona (TASK-2778)
  if (sub === '@') return zone
  // Defesa contra zona duplicada: se o sub já é/contém a zona (registro
  // reaproveitado como FQDN absoluto), não reconcatenar — era isso que
  // gerava zona.zona — ex. real no incidente 2026-07-21 (TASK-2778).
  if (sub === zone || sub.toLowerCase().endsWith(`.${zone.toLowerCase()}`)) return sub
  return `${sub}.${zone}`
}

/**
 * Deriva o subdomain relativo a partir de um registro do Route53 (TASK-2778).
 *
 * O Route53 devolve nomes como FQDN absoluto com trailing dot
 * ('zona.com.br.'). O apex da zona vira '@'; subdomínios viram o label
 * relativo ('www.zona.com.br.' → 'www'); registro fora da zona retorna
 * '' (inválido). Case-insensitive, tolera trailing dot na zona.
 */
export function deriveSubdomainFromRecord(recordName: string, zoneName: string | null): string {
  if (!recordName || !zoneName) return ''
  const record = recordName.trim().replace(/\.+$/, '').toLowerCase()
  const zone = zoneName.trim().replace(/\.+$/, '').toLowerCase()
  if (!record || !zone) return ''
  if (record === zone) return '@'
  if (record.endsWith(`.${zone}`)) return record.slice(0, -(zone.length + 1))
  return ''
}

/** Converte WizardState completo em JobPayload pra submit. Validacao server-side. */
export function toJobPayload(state: WizardState): JobPayload {
  const payload: JobPayload = {
    tenant_id: state.step1.tenant_id ?? 0,
    zone_id: state.step1.zone_id,
    record_name: state.step1.public_hostname,
    public_hostname: state.step1.public_hostname,
    slug: state.step2.slug,
    display_name: state.step2.display_name,
    environment: (state.step1.environment ?? 'DEV') as Environment,
    canonical_hostname: state.step2.canonical_hostname,
    protocol: state.step3.protocol,
    routing_mode: state.step3.routing_mode,
    identity_mode: state.step3.identity_mode,
    upstream_url: state.step3.upstream_url,
    required_permissions: state.step3.required_permissions,
    oidc_redirect_uris: state.step3.oidc_redirect_uris,
    org_default_visible: state.step3.org_default_visible,
    is_private: state.step3.is_private,
  }
  if (state.step3.allowed_hours) {
    payload.allowed_hours = state.step3.allowed_hours
    payload.bypass_time_window_roles = state.step3.bypass_time_window_roles
  }
  return payload
}

/** Quao "completo" cada step esta — usado pra liberar Continuar. */
export function isStepComplete(step: 1 | 2 | 3 | 4, state: WizardState): boolean {
  if (step === 1) {
    const s = state.step1
    return Boolean(s.tenant_id && s.zone_id && s.subdomain && s.public_hostname)
  }
  if (step === 2) {
    const s = state.step2
    return Boolean(isValidSlug(s.slug) && s.display_name.trim() && s.canonical_hostname.trim())
  }
  if (step === 3) {
    const s = state.step3
    if (!s.upstream_url.trim()) return false
    // upstream_url.host == public_hostname → loop apos migracao DNS Lote 3.
    // Backend Pydantic valida tambem; aqui bloqueia o "Continuar" inline.
    try {
      const upstreamHost = new URL(s.upstream_url).hostname.toLowerCase()
      const publicHost = state.step1.public_hostname?.toLowerCase()
      if (publicHost && upstreamHost === publicHost) return false
    } catch {
      return false
    }
    return true
  }
  if (step === 4) {
    return state.step4.confirmed_upstream_headers
  }
  return false
}
